import { z } from 'zod';

/** Bump when a stored shape changes; add a migration in db.ts. */
export const DATA_VERSION = 1;

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const ts = z.number().finite().nonnegative();
const unit = z.number().finite();

export const PatternSchema = z.enum(['grid', 'ruled', 'dots', 'plain']);
export type Pattern = z.infer<typeof PatternSchema>;

export const LineMarksSchema = z.object({
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
  heading: z.boolean().optional(),
});
export type LineMarks = z.infer<typeof LineMarksSchema>;

export const PrioritySchema = z.enum(['none', 'low', 'medium', 'high']);
export type Priority = z.infer<typeof PrioritySchema>;

export const LineSchema = z.object({
  id: z.string().min(1),
  text: z.string().max(20000),
  kind: z.enum(['text', 'check']),
  checked: z.boolean(),
  marks: LineMarksSchema.optional(),
  due: ts.nullable().optional(),
  priority: PrioritySchema.optional(),
});
export type Line = z.infer<typeof LineSchema>;

export const CropSchema = z.object({ t: unit, r: unit, b: unit, l: unit });
export type Crop = z.infer<typeof CropSchema>;

export const NoteImageSchema = z.object({
  id: z.string().min(1),
  assetId: z.string().min(1),
  /** Centre, as a fraction of the card's width (x) and height (y). */
  x: unit,
  y: unit,
  /** Width as a fraction of the card's width. */
  w: unit.positive(),
  /** Natural height / width of the (uncropped) image. */
  aspect: unit.positive(),
  rotation: unit,
  crop: CropSchema,
  z: z.number().int(),
  locked: z.boolean(),
  aboveText: z.boolean(),
});
export type NoteImage = z.infer<typeof NoteImageSchema>;

export const ToolSchema = z.enum(['pen', 'marker', 'crayon', 'highlighter', 'eraser']);
export type Tool = z.infer<typeof ToolSchema>;

export const StrokeSchema = z.object({
  id: z.string().min(1),
  tool: ToolSchema,
  color: hex,
  /** Brush diameter as a fraction of the card's width. */
  size: unit.positive(),
  /** Flat [x, y, pressure, …] with x and y as fractions of the card's width. */
  points: z.array(unit).max(60000),
});
export type Stroke = z.infer<typeof StrokeSchema>;

export const CompletedBehaviorSchema = z.enum(['strike', 'bottom', 'hide']);
export type CompletedBehavior = z.infer<typeof CompletedBehaviorSchema>;

export const NoteStyleSchema = z.object({
  paper: hex.optional(),
  ink: hex.optional(),
  line: hex.optional(),
  accent: hex.optional(),
  pattern: PatternSchema.optional(),
  font: z.string().optional(),
  /** Font size in % of the card width (CSS `cqw`). */
  fontScale: z.number().min(1.5).max(20),
  autoSize: z.boolean(),
  /** `block` (reference default): the text block is centred, its lines are left-aligned. */
  align: z.enum(['block', 'left', 'center', 'right']),
  vAlign: z.enum(['top', 'center']),
  lineHeight: z.number().min(0.9).max(2.4),
  compact: z.boolean(),
  watermark: z.boolean(),
  completed: CompletedBehaviorSchema,
});
export type NoteStyle = z.infer<typeof NoteStyleSchema>;

export const NoteSchema = z.object({
  id: z.string().min(1),
  version: z.literal(DATA_VERSION),
  title: z.string().max(200),
  lines: z.array(LineSchema).min(1).max(2000),
  images: z.array(NoteImageSchema).max(50),
  strokes: z.array(StrokeSchema).max(5000),
  designId: z.string().min(1),
  style: NoteStyleSchema,
  tags: z.array(z.string().max(40)).max(30),
  pinned: z.boolean(),
  favorite: z.boolean(),
  archived: z.boolean(),
  deletedAt: ts.nullable(),
  order: z.number().finite(),
  createdAt: ts,
  updatedAt: ts,
  rev: z.number().int().nonnegative(),
});
export type Note = z.infer<typeof NoteSchema>;

export const BorderSchema = z.object({
  style: z.enum(['none', 'solid', 'dashed', 'double', 'stitch']),
  color: hex,
  /** Fraction of the card width. */
  width: z.number().min(0).max(0.05),
});
export type Border = z.infer<typeof BorderSchema>;

export const DecorationSchema = z.enum(['none', 'tape', 'pin', 'corner', 'stars', 'hearts', 'leaves', 'snow', 'sun', 'margin', 'binder', 'sparkle']);
export type Decoration = z.infer<typeof DecorationSchema>;

export const DesignSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(60),
  category: z.string().min(1).max(40),
  source: z.enum(['reference', 'original', 'custom']),
  paper: hex,
  line: hex,
  ink: hex,
  accent: hex,
  pattern: PatternSchema,
  /** Pattern pitch multiplier; 1 = 1/20 of the card width (reference value). */
  patternScale: z.number().min(0.5).max(3),
  /** Opacity of the paper (reference "Clear" designs are translucent). */
  paperAlpha: z.number().min(0).max(1).optional(),
  lineAlpha: z.number().min(0).max(1).optional(),
  font: z.string().min(1),
  border: BorderSchema.optional(),
  decoration: DecorationSchema.optional(),
  createdAt: ts.optional(),
  updatedAt: ts.optional(),
});
export type Design = z.infer<typeof DesignSchema>;

export const AssetSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(['image', 'font']),
  mime: z.string().max(100),
  name: z.string().max(200).optional(),
  size: z.number().int().nonnegative(),
  width: z.number().optional(),
  height: z.number().optional(),
  hash: z.string(),
  createdAt: ts,
  /** Raw bytes. ArrayBuffer rather than Blob: structured-clones everywhere and avoids old Safari Blob-in-IDB bugs. */
  data: z.instanceof(ArrayBuffer),
});
export type Asset = z.infer<typeof AssetSchema>;

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const timeStr = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const RecurrenceSchema = z.object({
  kind: z.enum(['once', 'daily', 'weekly', 'monthly']),
  /** Local date for `once`; ignored otherwise. */
  date: dateStr,
  time: timeStr,
  interval: z.number().int().min(1).max(365),
  /** 0 = Sunday … 6 = Saturday. */
  weekdays: z.array(z.number().int().min(0).max(6)).max(7),
  monthDay: z.number().int().min(1).max(31),
  startDate: dateStr,
  endDate: dateStr.nullable(),
  timeZone: z.string().min(1),
});
export type Recurrence = z.infer<typeof RecurrenceSchema>;

export const ScheduleSchema = z.object({
  id: z.string().min(1),
  noteId: z.string().min(1),
  title: z.string().max(120),
  message: z.string().max(500),
  enabled: z.boolean(),
  rule: RecurrenceSchema,
  notify: z.boolean(),
  leadMinutes: z.number().int().min(0).max(1440),
  switchNote: z.boolean(),
  /** Minutes the note stays on screen after a switch; null = until the next switch. */
  durationMinutes: z.number().int().min(1).max(10080).nullable(),
  fallback: z.object({ kind: z.enum(['previous', 'note']), noteId: z.string().optional() }),
  snoozedUntil: ts.nullable(),
  createdAt: ts,
  updatedAt: ts,
});
export type Schedule = z.infer<typeof ScheduleSchema>;

export const DeliverySchema = z.object({
  key: z.string().min(1),
  scheduleId: z.string(),
  noteId: z.string(),
  occurrence: ts,
  firedAt: ts,
  status: z.enum(['delivered', 'missed', 'dismissed', 'snoozed', 'completed', 'suppressed']),
  channel: z.enum(['system', 'in-app', 'none']),
});
export type Delivery = z.infer<typeof DeliverySchema>;

export const SnapshotSchema = z.object({
  id: z.string().min(1),
  noteId: z.string().min(1),
  createdAt: ts,
  reason: z.enum(['auto', 'manual', 'restore', 'conflict', 'delete', 'import']),
  label: z.string().max(80).optional(),
  text: z.string(),
  chars: z.number().int().nonnegative(),
  note: NoteSchema,
});
export type Snapshot = z.infer<typeof SnapshotSchema>;

export const WallpaperIdSchema = z.string();

export const SettingsSchema = z.object({
  defaultDesignId: z.string(),
  defaultFont: z.string(),
  watermark: z.boolean(),
  autoSize: z.boolean(),
  compactDefault: z.boolean(),
  completedBehavior: CompletedBehaviorSchema,
  quietHours: z.object({ enabled: z.boolean(), start: timeStr, end: timeStr }),
  notificationsOptIn: z.boolean(),
  history: z.object({
    autoSnapshotMinutes: z.number().int().min(1).max(1440),
    maxPerNote: z.number().int().min(1).max(500),
    maxAgeDays: z.number().int().min(1).max(3650),
  }),
  favoriteDesigns: z.array(z.string()).max(500),
  sort: z.enum(['updated', 'created', 'title', 'custom']),
  lockPreview: z.object({
    wallpaper: z.string(),
    customWallpaperAssetId: z.string().nullable(),
    position: z.number().min(0).max(1),
    scale: z.number().min(0.6).max(1),
    device: z.string(),
    contrast: z.boolean(),
    showClock: z.boolean(),
  }),
  customFonts: z.array(z.object({ id: z.string(), family: z.string(), assetId: z.string() })).max(50),
  installHintDismissed: z.boolean(),
  /** Reference setting: remove a checked line one second after it is ticked (history snapshot first). */
  deleteCheckedLines: z.boolean(),
  /** Reference setting: show the note on the (simulated) lock screen even when it is empty. */
  showEmptyNotes: z.boolean(),
  /** Reference "Note Template": what a new page starts with. */
  newPageTemplate: z.string(),
  tutorialSeen: z.boolean(),
});
export type Settings = z.infer<typeof SettingsSchema>;

export const MetaSchema = z.object({
  firstUseAt: ts,
  /** The note shown on the editor home / lock-screen preview when no schedule overrides it. */
  activeNoteId: z.string().nullable(),
  lastSchedulerCheck: ts,
});
export type Meta = z.infer<typeof MetaSchema>;

export const BackupSchema = z.object({
  app: z.literal('memonest'),
  dataVersion: z.number().int().min(1).max(DATA_VERSION),
  exportedAt: ts,
  notes: z.array(z.unknown()),
  designs: z.array(z.unknown()),
  schedules: z.array(z.unknown()),
  snapshots: z.array(z.unknown()).optional(),
  assets: z.array(
    z.object({
      id: z.string(),
      kind: z.enum(['image', 'font']),
      mime: z.string(),
      name: z.string().optional(),
      width: z.number().optional(),
      height: z.number().optional(),
      dataUrl: z.string(),
    }),
  ),
  settings: z.unknown().optional(),
  meta: z.unknown().optional(),
});
export type Backup = z.infer<typeof BackupSchema>;
